import {EditorSDK, PageRef} from '@wix/platform-editor-sdk';
import {AppReflowType, PageMap, RouterPrefix} from '@wix/wixstores-client-core';
import {EcomPublicApi} from '../../editor-script/editorScript';
import {AppReflowPageVariantID, AppReflowPremiumFeatureName} from '../constants';
import {getNumOfPagesByTpaPageId} from '../utils/pages';
import {doTransaction} from '../../editor-script/transaction';
import {Logger as WebBiLogger} from '@wix/web-bi-logger/dist/src/types';
import {guid} from '@wix/web-bi-logger/dist/src/lib/utils';
import {BiStoresReflowPageName, RuleDef} from './types';
import {sendBi} from './bi';
import {createRule, openRulesModal} from './rulesModal';
import {addReflowPage} from './pages';
import {IHttpClient} from '@wix/http-client';
import {reportPremiumQuotaUsage} from './premium';

export const addProductPageVariant = async ({
  editorSDK,
  httpClient,
  variantId,
  ecomPublicApi,
  pageRefToDuplicate,
  t,
  webBiLogger,
  errorReporter,
  metaSiteId,
}: {
  editorSDK: EditorSDK;
  httpClient: IHttpClient;
  variantId: AppReflowPageVariantID;
  ecomPublicApi: EcomPublicApi;
  pageRefToDuplicate?: PageRef;
  t: (key: string) => string;
  webBiLogger: WebBiLogger;
  errorReporter: (e: any) => any;
  metaSiteId: string;
}) => {
  try {
    const biFlowId = guid();

    await editorSDK.editor.openProgressBar('', {
      title: t('newProductPage.loader.main.title'),
      totalSteps: 3,
      currentStep: 0,
      stepTitle: t('newProductPage.loader.stepOne.subtitle'),
    });

    const pageRef = await editorSDK.tpa.getPageRefByTPAPageId('', {
      tpaPageId: PageMap.PRODUCT,
    });

    await editorSDK.editor.updateProgressBar('', {
      currentStep: 1,
      stepTitle: t('newProductPage.loader.stepOne.subtitle'),
    });

    await doTransaction(
      editorSDK,
      async () => {
        const routerRef = await ecomPublicApi.getRouterRefByPrefix(RouterPrefix.PRODUCT_PAGE);

        if (!routerRef) {
          await addProductPageRouter(editorSDK, ecomPublicApi, pageRef);
        }
      },
      errorReporter
    );

    const emptyRule = createRule();

    await doTransaction(
      editorSDK,
      async () => {
        const numOfProductPageVariants = await getNumOfPagesByTpaPageId(editorSDK, PageMap.PRODUCT_PAGE_VARIANT);

        const newPage: any = await addReflowPage(
          editorSDK,
          variantId,
          pageRefToDuplicate?.id ?? pageRef.id,
          'Product Page'
        );

        await editorSDK.editor.updateProgressBar('', {
          currentStep: 2,
          stepTitle: t('newProductPage.loader.stepTwo.subtitle'),
        });

        const variantPageRef = {id: newPage.pageId, type: 'DESKTOP'} as PageRef;

        const newPageTitle = `Product Page ${numOfProductPageVariants + 1}`;

        await editorSDK.editor.routers.addVariantToPage({
          pageRef,
          variantPageRef,
          rule: emptyRule,
          variantId,
          biFlowId,
        });

        await editorSDK.pages.navigateTo('', {pageRef: newPage});

        await editorSDK.editor.updateProgressBar('', {
          currentStep: 3,
          stepTitle: t('newProductPage.loader.stepThree.subtitle'),
        });

        await reportPremiumQuotaUsage(AppReflowPremiumFeatureName.ProductPage, httpClient, metaSiteId, '1');

        await editorSDK.pages.data.update('', {
          pageRef: newPage,
          data: {
            tpaPageId: PageMap.PRODUCT_PAGE_VARIANT,
            title: newPageTitle,
          },
        });

        await ecomPublicApi.setStateForStoresPages();

        await editorSDK.editor.closeProgressBar('', {});

        const ruleDef: RuleDef = await openRulesModal(
          editorSDK,
          ecomPublicApi,
          t,
          newPageTitle,
          metaSiteId,
          AppReflowType.ProductPage
        );

        if (ruleDef?.type) {
          const updatedRule = createRule(ruleDef);
          await (editorSDK.editor.routers as any).updateVariantPageRule({variantPageRef, updatedRule, biFlowId});
        }

        return sendBi({
          biStoresReflowPageName: BiStoresReflowPageName.ProductPage,
          webBiLogger,
          isEdit: false,
          pageVariantId: variantPageRef.id,
          originalPageId: pageRef.id,
          duplicatedPageId: pageRefToDuplicate?.id,
          pageVariantType: variantId,
          ruleDef,
          flowId: biFlowId,
          msid: metaSiteId,
        });
      },
      errorReporter
    );
  } catch (e) {
    errorReporter(e);
  }
};

async function addProductPageRouter(editorSDK: EditorSDK, ecomPublicApi: EcomPublicApi, pageRef: PageRef) {
  const pageData = await editorSDK.pages.data.get('', {
    pageRef,
  });
  const URI = pageData.pageUriSEO;
  await editorSDK.pages.data.update('', {
    pageRef,
    data: {
      pageUriSEO: `${URI}-static`,
    },
  });

  const routerRef = await ecomPublicApi.addRouter(URI);

  await ecomPublicApi.connectPageToRouter({
    pageRef,
    routerRef,
    pageRoles: [RouterPrefix.PRODUCT_PAGE],
  });
  return routerRef;
}
